import { useCallback, useEffect, useRef } from "react";

import { useSaveEntityFreeTextMutation } from "../../hooks/api/use-save-entity-free-text";
import { type SaveFieldResponseInput, useSaveFieldResponseMutation } from "../../hooks/api/use-save-field-response";

import { saveFieldDraft } from "./use-draft-persistence";

const FREE_TEXT_TIMER_KEY = "__freeText";
const DEBOUNCE_MS = 500;

interface UseAutoSaveProps {
  dataRequestEntityId: string;
}

export function useAutoSave({ dataRequestEntityId }: UseAutoSaveProps) {
  const { saveFieldResponseAsync } = useSaveFieldResponseMutation();
  const { saveFreeTextAsync } = useSaveEntityFreeTextMutation();
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const pendingCallbacksRef = useRef<Record<string, SaveFieldResponseInput>>({});
  const pendingFreeTextRef = useRef<Record<string, string> | null>(null);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of Object.values(timers)) {
        clearTimeout(timer);
      }
    };
  }, []);

  const flushFreeText = useCallback(async () => {
    if (pendingFreeTextRef.current === null) return;
    const answers = pendingFreeTextRef.current;
    pendingFreeTextRef.current = null;
    await saveFreeTextAsync({ dataRequestEntityId, answers }).catch(() => undefined);
  }, [dataRequestEntityId, saveFreeTextAsync]);

  const saveFreeText = useCallback(
    (answers: Record<string, string>) => {
      pendingFreeTextRef.current = answers;

      if (timersRef.current[FREE_TEXT_TIMER_KEY]) {
        clearTimeout(timersRef.current[FREE_TEXT_TIMER_KEY]);
      }
      timersRef.current[FREE_TEXT_TIMER_KEY] = setTimeout(() => {
        delete timersRef.current[FREE_TEXT_TIMER_KEY];
        flushFreeText();
      }, DEBOUNCE_MS);
    },
    [flushFreeText],
  );

  const saveField = useCallback(
    (input: { fieldKey: string; newValue?: unknown | null; notPossible?: boolean; comment?: string | null }) => {
      const timerKey = `${dataRequestEntityId}-${input.fieldKey}`;

      if (timersRef.current[timerKey]) {
        clearTimeout(timersRef.current[timerKey]);
      }

      const saveInput: SaveFieldResponseInput = {
        dataRequestEntityId,
        fieldKey: input.fieldKey,
        newValue: input.newValue,
        notPossible: input.notPossible ?? false,
        comment: input.comment,
      };

      pendingCallbacksRef.current[timerKey] = saveInput;
      timersRef.current[timerKey] = setTimeout(() => {
        saveFieldDraft(dataRequestEntityId, input.fieldKey, {
          newValue: input.newValue,
          notPossible: input.notPossible,
          comment: input.comment,
        });
        saveFieldResponseAsync(saveInput).catch(() => undefined);
        delete timersRef.current[timerKey];
        delete pendingCallbacksRef.current[timerKey];
      }, DEBOUNCE_MS);
    },
    [dataRequestEntityId, saveFieldResponseAsync],
  );

  const flush = useCallback(async () => {
    const entries = Object.entries(timersRef.current);
    for (const [, timer] of entries) {
      clearTimeout(timer);
    }
    const pendingSaves: Promise<unknown>[] = [];
    for (const [entryKey] of entries) {
      const pendingInput = pendingCallbacksRef.current[entryKey];
      if (pendingInput) {
        pendingSaves.push(saveFieldResponseAsync(pendingInput).catch(() => undefined));
        delete pendingCallbacksRef.current[entryKey];
      }
    }
    timersRef.current = {};
    pendingSaves.push(flushFreeText());
    await Promise.all(pendingSaves);
  }, [saveFieldResponseAsync, flushFreeText]);

  return { saveField, saveFreeText, flush };
}
